<template>
  <div class="container mt-4">
    <TopNav
      :title="$t('MEDIA_LIBRARY.TITLE.LIBRARY')"
      :showOpenMainNav="true"
      :icon="['far', 'folder-open']"
    >
      <NavItem
        icon="fal fa-plus"
        :text="$t('MEDIA_LIBRARY.BUTTON.ADD_FILE')"
        @click.native="addFiles"
      />
    </TopNav>
    <ExpiredStudio v-if="teacher.is_expired" :isTeacher="true" />
    <div v-else>
      <div
        v-if="!canUploadMoreVideos"
        class="alert-danger px-3 py-2 rounded mb-3 c-charcoal small"
      >
        <router-link to="/settings/adjust-plan">
          <span class="mr-2">
            <img src="/assets/images/rocket.svg" height="24px" />
          </span>
          <span>
            {{ $t('MEDIA_LIBRARY.TEXT.UPGRADE_TO_UPLOAD_MORE_VIDEOS') }}
          </span>
        </router-link>
      </div>
      <section class="media-search-section" v-if="form">
        <div class="d-flex">
          <HMSearch class="w-100" v-model="form.search" />
          <slot name="search-left"></slot>
        </div>
        <div class="row mt-3">
          <div class="col-6">
            <v-select
              :placeholder="$t('MEDIA_LIBRARY.LABEL.ALL_TYPES')"
              :options="fileContentList"
              v-model="form.fileContentSelected"
            />
          </div>
          <div class="col-6">
            <v-select
              :placeholder="$t('MEDIA_LIBRARY.LABEL.ALL_INSTRUCTORS')"
              :options="instructors"
              label="name"
              code="id"
              v-model="form.instructorsSelected"
            />
          </div>
        </div>
        <div class="row mt-3">
          <div class="col">
            <v-select
              :placeholder="$t('MEDIA_LIBRARY.LABEL.ALL_TAGS')"
              :options="categoriesTags"
              multiple
              label="name"
              code="id"
              v-model="form.tagsSelected"
            />
          </div>
        </div>
      </section>

      <HMPaginatedTable
        ref="media-table"
        api="mediaLibrary/getMedia"
        :query="searchQuery"
        name="media-library-table"
        :pageInQuery="true"
        :perPageFixed="12"
        v-if="form"
      >
        <template slot-scope="{ data }">
          <div class="mt-4 row">
            <div
              class="column col-md-4"
              v-for="mediaItem in data.res.results"
              :key="mediaItem.id"
            >
              <MediaItemCard @click="openMedia(mediaItem)" :media="mediaItem" />
            </div>
          </div>
        </template>
        <template v-slot:no-data>
          <div class="text-center">
            <HMEmpty
              image="empty-media-library"
              :title="$t('MEDIA_LIBRARY.TEXT.EMPTY_MEDIA_LIBRARY_TITLE')"
              :description="
                $t('MEDIA_LIBRARY.TEXT.EMPTY_MEDIA_LIBRARY_DESCRIPTION')
              "
            />
            <button
              @click="addFiles"
              type="button"
              name="button"
              class="btn btn-primary"
            >
              {{ $t('MEDIA_LIBRARY.BUTTON.UPLOAD_FILE') }}
            </button>
          </div>
        </template>
      </HMPaginatedTable>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import TopNav from '@/web/components/navigation/TopNav.vue';
import NavItem from '@/web/components/navigation/NavItem.vue';
import MediaFormModal from '@/web/components/media-library/MediaFormModal.vue';
import MediaItemCard from '@/web/components/media-library/MediaItemCard.vue';
import ExpiredStudio from '@/web/components/shop/ExpiredStudio';
import { errorToMessage } from '@/core/utils/errors';
import TeacherPermissionsMixin from '@/web/mixins/TeacherPermissionsMixin';

function getEmptyForm() {
  return {
    search: '',
    fileContentSelected: null,
    instructorsSelected: null,
    tagsSelected: [],
  };
}

export default {
  components: {
    TopNav,
    NavItem,
    MediaItemCard,
    ExpiredStudio,
  },
  mixins: [TeacherPermissionsMixin],
  data() {
    return {
      fileContentList: [
        {
          label: this.$t('GENERAL.LABEL.DOCUMENTS'),
          code: 'application',
        },
        {
          label: this.$t('GENERAL.LABEL.VIDEO'),
          code: 'video',
        },
        {
          label: this.$t('GENERAL.LABEL.AUDIO'),
          code: 'audio',
        },
      ],
      form: undefined,
      canUploadMoreVideos: true,
    };
  },
  computed: {
    ...mapState('mediaLibrary', ['instructors']),
    ...mapGetters('mediaLibrary', ['media', 'categoriesTags']),
    ...mapGetters('user', ['teacher']),
    searchQuery() {
      return {
        q: this.form.search ? this.form.search : undefined,
        fileType: this.form.fileContentSelected?.code,
        instructor: this.form.instructorsSelected?.id,
        options: this.form.tagsSelected.map(option => option.id).join(','),
      };
    },
  },
  methods: {
    addFiles() {
      this.$modal.show(
        MediaFormModal,
        {
          refreshMedia: this.$refs['media-table'].reload,
          videoUploadAllowed: this.videoUploadAllowed,
        },
        { clickToClose: false }
      );
    },
    openMedia(media) {
      this.$router.push({
        name: 'media-library-view-item',
        params: { media_id: media.id },
      });
    },
    async loadData() {
      try {
        this.canUploadMoreVideos = true;
        await this.$store.dispatch('mediaLibrary/canUploadVideo');
      } catch (error) {
        this.canUploadMoreVideos = false;
      }
    },
  },
  async mounted() {
    this.form =
      (await this.$store.dispatch('filters/getFilter', 'media-table')) ||
      getEmptyForm();
    try {
      this.$store.dispatch('mediaLibrary/getInstructors');
      this.$store.dispatch('mediaLibrary/getCategories');
      this.loadData();
    } catch (error) {
      this.$notify({
        group: 'app',
        type: 'error',
        title: 'Failed to load media library',
        text: errorToMessage(error.response, `Request failed: ${error}`),
        duration: -1, // stay ON
      });
    }
  },
  beforeDestroy() {
    this.$store.dispatch('filters/setFilter', {
      key: 'media-table',
      filter: JSON.parse(JSON.stringify(this.form)),
    });
  },
};
</script>

<style lang="scss" scoped></style>
